import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { schedulesService } from '../services/firebase/schedules.service';

export interface EmployeeShift {
  scheduleId: string;
  shopId: string;
  date: string;
  startTime: string;
  endTime: string; // Calculated from startTime + duration
  duration: number; // Hours
  workingHoursId: string;
}

/**
 * Custom hook for fetching an employee's schedule
 * Extracts the employee's shifts from shop schedules
 */
export function useEmployeeSchedules(
  employeeId: string | undefined,
  startDate: string,
  endDate: string
) {
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  // Fetch all schedules in the date range
  const {
    data: allSchedules = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['employeeSchedules', organizationId, employeeId, startDate, endDate],
    queryFn: async () => {
      if (!organizationId || !employeeId) return [];
      return schedulesService.getSchedulesByDateRange(organizationId, startDate, endDate);
    },
    enabled: !!organizationId && !!employeeId && !!startDate && !!endDate,
  });

  // Extract this employee's shifts from all schedules
  const schedules = useMemo(() => {
    if (!employeeId) return [];

    const employeeShifts: EmployeeShift[] = [];

    allSchedules.forEach((schedule) => {
      // Find employee's schedule in this shop schedule
      const employeeSchedule = schedule.employeeSchedules?.find(
        (es) => es.employeeId === employeeId
      );

      if (employeeSchedule?.workingHours) {
        // Add each working hours entry as a shift
        employeeSchedule.workingHours.forEach((wh) => {
          // Calculate end time from start time and duration
          const [startH, startM] = wh.startTime.split(':').map(Number);
          const totalMinutes = startH * 60 + startM + wh.duration * 60;
          const endH = Math.floor(totalMinutes / 60) % 24;
          const endM = totalMinutes % 60;
          const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;

          employeeShifts.push({
            scheduleId: schedule.id,
            shopId: schedule.shopId,
            date: schedule.date,
            startTime: wh.startTime,
            endTime,
            duration: wh.duration,
            workingHoursId: wh.id,
          });
        });
      }
    });

    // Sort by date and start time
    employeeShifts.sort((a, b) => {
      if (a.date !== b.date) {
        return a.date.localeCompare(b.date);
      }
      return a.startTime.localeCompare(b.startTime);
    });

    return employeeShifts;
  }, [allSchedules, employeeId]);

  return {
    schedules,
    isLoading,
    error,
    refetch,
  };
}
